/**
 * Letterheads — Settings > Documents & Templates > Letterheads.
 *
 * Phase 1: upload / list / preview / set-default / rename / duplicate /
 * archive / delete. The visual field-overlay editor lives in Phase 2.
 *
 * Design principle: every clinic can have a COMPLETELY different
 * letterhead. We render each uploaded asset as-is (PDF via <iframe> for
 * pixel-accurate preview, images via <img>) and never re-flow the design.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, FileText, Star, Copy, Archive as ArchiveIcon,
  Trash2, MoreHorizontal, Loader2, ImageIcon, Check, X, Edit2, Sparkles,
  CheckCircle2, CircleDot, Ban,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { apiError } from "../../lib/apiError";

const CATEGORIES = [
  { value: "prescription",         label: "Prescription" },
  { value: "consultation",         label: "Consultation" },
  { value: "treatment_plan",       label: "Treatment Plan" },
  { value: "medical_certificate",  label: "Medical Certificate" },
  { value: "referral",             label: "Referral" },
  { value: "investigation",        label: "Investigation" },
  { value: "xray_report",          label: "X-Ray Report" },
  { value: "consent",              label: "Consent" },
  { value: "post_op",              label: "Post-Op Instructions" },
  { value: "custom",               label: "Custom" },
];

const ACCEPT_TYPES = ".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf";
const MAX_MB = 15;

const backendBase = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const assetHref = (url) => `${backendBase()}${url}`;

export default function Letterheads() {
  const navigate = useNavigate();
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewOf, setPreviewOf] = useState(null);
  const [renameOf, setRenameOf]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/letterheads?include_archived=true");
      setRows(data.letterheads || []);
    } catch (e) { toast.error(apiError(e, "Failed to load letterheads")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (id) => {
    try {
      await api.patch(`/letterheads/${id}`, { set_default: true });
      toast.success("Default letterhead updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (id, archived) => {
    try {
      await api.patch(`/letterheads/${id}`, { archive: archived });
      toast.success(archived ? "Archived" : "Restored");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (id) => {
    try {
      await api.post(`/letterheads/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this letterhead? Historical documents keep their frozen snapshot.")) return;
    try {
      await api.delete(`/letterheads/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Activation lifecycle — the operator reviews the detected fields in the
  // editor, then hits "Accept & Activate" to make this letterhead the ONE
  // active custom template for the clinic. This is what causes the
  // "Custom Letterhead" option to appear on the Prescription download menu.
  const activate = async (id) => {
    try {
      await api.post(`/letterheads/${id}/activate`);
      toast.success("Letterhead activated — now available on prescriptions");
      load();
    } catch (e) { toast.error(apiError(e, "Could not activate letterhead")); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this letterhead? New prescriptions will fall back to the Viyora digital template.")) return;
    try {
      await api.post(`/letterheads/${id}/deactivate`);
      toast.success("Letterhead deactivated");
      load();
    } catch (e) { toast.error(apiError(e, "Could not deactivate")); }
  };

  // Auto-analyze via Claude Sonnet 4.5 vision — detects layout regions,
  // returns field placements with confidence scores, and takes the operator
  // into the editor with the proposals pre-loaded for review.
  const autoAnalyze = async (id) => {
    const tid = toast.loading("Analyzing layout with vision AI…", { duration: 60000 });
    try {
      const { data } = await api.post(`/letterheads/${id}/analyze`, {});
      toast.dismiss(tid);
      if (!data.ok || !data.field_count) {
        toast.error(data.error || "Analysis returned no fields — try uploading a sharper image, or add fields manually.");
        return;
      }
      // Stash the proposal for the editor to pick up.
      try {
        sessionStorage.setItem(
          `lh_analysis_${id}`,
          JSON.stringify({ at: Date.now(), model: data.model_used, fields: data.fields }),
        );
      } catch { /* private-browsing quota */ }
      toast.success(`Detected ${data.field_count} fields — review in editor`);
      navigate(`/app/settings/documents/letterheads/${id}/edit?analysis=1`);
    } catch (e) { toast.dismiss(tid); toast.error(apiError(e, "Analysis failed")); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="letterheads-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/app/settings" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Settings
          </Link>
          <h1 className="text-2xl font-medium text-ink" data-testid="letterheads-title">Letterheads</h1>
          <p className="mt-1 text-sm text-ink-mid max-w-2xl">
            Upload the clinic's letterhead once — Viyora places dynamic patient / clinical / signature fields on top of it. Every clinic can have a completely different design.
          </p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors shrink-0"
          data-testid="letterheads-add-btn"
        >
          <Upload className="w-4 h-4" /> Add Letterhead
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-3xl bg-white border border-[#E7E1D3] p-10 text-center text-sm text-ink-mid">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading letterheads…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onAdd={() => setUploadOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="letterheads-grid">
          {rows.map((lh) => (
            <LetterheadCard
              key={lh.id}
              lh={lh}
              onPreview={() => setPreviewOf(lh)}
              onSetDefault={() => setDefault(lh.id)}
              onDuplicate={() => duplicate(lh.id)}
              onArchive={(a) => archive(lh.id, a)}
              onDelete={() => remove(lh.id)}
              onRename={() => setRenameOf(lh)}
              onEditFields={() => navigate(`/app/settings/documents/letterheads/${lh.id}/edit`)}
              onAutoAnalyze={() => autoAnalyze(lh.id)}
              onActivate={() => activate(lh.id)}
              onDeactivate={() => deactivate(lh.id)}
            />
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadDialog onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); load(); }} />
      )}
      {previewOf && (
        <PreviewDialog lh={previewOf} onClose={() => setPreviewOf(null)} />
      )}
      {renameOf && (
        <RenameDialog lh={renameOf} onClose={() => setRenameOf(null)} onDone={() => { setRenameOf(null); load(); }} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ card */
const STATUS_STYLES = {
  draft:           { label: "Draft",           cls: "bg-slate-100 text-slate-700 border-slate-200",     Icon: CircleDot },
  pending_review:  { label: "Pending Review",  cls: "bg-amber-50 text-amber-800 border-amber-200",       Icon: Sparkles },
  active:          { label: "Active",          cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  inactive:        { label: "Inactive",        cls: "bg-slate-50 text-slate-500 border-slate-200",       Icon: Ban },
};

function LetterheadCard({ lh, onPreview, onSetDefault, onDuplicate, onArchive, onDelete, onRename, onEditFields, onAutoAnalyze, onActivate, onDeactivate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const geo = lh.geometry || {};
  const pageSize = geo.detected_page_size || "—";
  const orient = geo.orientation === "landscape" ? "Landscape" : "Portrait";
  const status = lh.status || (lh.fields?.length ? "pending_review" : "draft");
  const st = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const hasFields = (lh.fields?.length || 0) > 0;
  return (
    <div
      className={`rounded-3xl bg-white border p-5 transition-colors ${lh.is_archived ? "border-[#E7E1D3] opacity-60" : status === "active" ? "border-emerald-300 ring-1 ring-emerald-200" : "border-[#E7E1D3] hover:border-ink/30"}`}
      data-testid={`letterhead-card-${lh.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-medium text-ink truncate" data-testid={`lh-name-${lh.id}`}>{lh.name}</h3>
            <span
              className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${st.cls}`}
              data-testid={`lh-status-${lh.id}`}
            >
              <st.Icon className="w-3 h-3" /> {st.label}
            </span>
            {lh.is_archived && (
              <span className="text-[10px] uppercase tracking-wider text-ink-mid bg-linen border border-[#E7E1D3] px-2 py-0.5 rounded-full">Archived</span>
            )}
          </div>
          <div className="mt-1.5 text-xs text-ink-mid flex flex-wrap gap-x-3 gap-y-1">
            <span>{pageSize} · {orient}</span>
            <span>·</span>
            <span className="uppercase text-[10px] tracking-wider">{lh.asset?.ext}</span>
            <span>·</span>
            <span>{CATEGORIES.find(c => c.value === lh.category)?.label || lh.category}</span>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="p-1.5 rounded-full hover:bg-linen text-ink-mid"
            data-testid={`lh-more-${lh.id}`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-[#E7E1D3] rounded-2xl shadow-lg z-10 min-w-[200px] py-1"
                 onMouseLeave={() => setMenuOpen(false)}>
              <MenuItem icon={Edit2}  label="Rename"          onClick={() => { setMenuOpen(false); onRename(); }} testid={`lh-rename-${lh.id}`} />
              {status !== "active" && hasFields && (
                <MenuItem icon={CheckCircle2} label="Accept & Activate" onClick={() => { setMenuOpen(false); onActivate(); }} testid={`lh-activate-${lh.id}`} />
              )}
              {status === "active" && (
                <MenuItem icon={Ban} label="Deactivate" onClick={() => { setMenuOpen(false); onDeactivate(); }} testid={`lh-deactivate-${lh.id}`} />
              )}
              <MenuItem icon={Copy}      label="Duplicate"        onClick={() => { setMenuOpen(false); onDuplicate(); }} testid={`lh-dup-${lh.id}`} />
              <MenuItem icon={ArchiveIcon} label={lh.is_archived ? "Restore" : "Archive"} onClick={() => { setMenuOpen(false); onArchive(!lh.is_archived); }} testid={`lh-arch-${lh.id}`} />
              {status !== "active" && (
                <MenuItem icon={Trash2} label="Delete" onClick={() => { setMenuOpen(false); onDelete(); }} danger testid={`lh-del-${lh.id}`} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail */}
      <button
        onClick={onPreview}
        className="mt-4 block w-full aspect-[210/297] rounded-2xl overflow-hidden bg-linen border border-[#E7E1D3] hover:border-ink/30 transition-colors"
        data-testid={`lh-preview-${lh.id}`}
        title="Click to preview at full size"
      >
        <LetterheadThumbnail lh={lh} />
      </button>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button onClick={onPreview}
                 className="flex-1 min-w-[80px] text-xs font-medium text-ink bg-linen hover:bg-[#E7E1D3] px-3 py-2 rounded-full transition-colors"
                 data-testid={`lh-preview-btn-${lh.id}`}>
          Preview
        </button>
        <button
          onClick={onAutoAnalyze}
          className="flex-1 min-w-[110px] text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 px-3 py-2 rounded-full transition-colors inline-flex items-center justify-center gap-1"
          data-testid={`lh-auto-analyze-${lh.id}`}
          title="Vision AI detects field positions from your uploaded design"
        >
          <Sparkles className="w-3 h-3" /> Auto-analyze
        </button>
        <button
          onClick={onEditFields}
          className="flex-1 min-w-[110px] text-xs font-medium text-ink bg-linen hover:bg-[#E7E1D3] px-3 py-2 rounded-full transition-colors inline-flex items-center justify-center gap-1"
          data-testid={`lh-edit-fields-${lh.id}`}
        >
          <Edit2 className="w-3 h-3" /> {status === "pending_review" ? "Review Layout" : "Edit Fields"}
          {hasFields && (
            <span className="text-[10px] opacity-70">· {lh.fields.length}</span>
          )}
        </button>
        {status !== "active" && hasFields && (
          <button
            onClick={onActivate}
            className="w-full text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-full transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid={`lh-activate-btn-${lh.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Accept &amp; Activate
          </button>
        )}
        {status === "active" && (
          <button
            onClick={onDeactivate}
            className="w-full text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-full transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid={`lh-deactivate-btn-${lh.id}`}
          >
            <Ban className="w-3.5 h-3.5" /> Deactivate
          </button>
        )}
      </div>
    </div>
  );
}

function LetterheadThumbnail({ lh }) {
  const src = assetHref(`/api/public/letterhead-asset/${lh.id}`);
  if (lh.asset?.ext === "pdf") {
    // Inline PDF as image via <iframe> — works cross-browser without a viewer.
    return (
      <iframe src={`${src}#toolbar=0&navpanes=0&scrollbar=0`}
              title={`${lh.name} preview`}
              className="w-full h-full pointer-events-none"
              data-testid={`lh-thumb-pdf-${lh.id}`} />
    );
  }
  return (
    <img src={src} alt={lh.name} className="w-full h-full object-contain" data-testid={`lh-thumb-img-${lh.id}`} />
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger = false }) {
  return (
    <button onClick={onClick}
            data-testid={testid}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-linen transition-colors ${danger ? "text-red-600" : "text-ink"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/* ---------------------------------------------------------- empty state */
function EmptyState({ onAdd }) {
  return (
    <div className="rounded-3xl bg-white border border-dashed border-[#E7E1D3] p-10 text-center" data-testid="letterheads-empty">
      <div className="w-14 h-14 rounded-2xl bg-linen border border-[#E7E1D3] flex items-center justify-center mx-auto mb-4">
        <FileText className="w-6 h-6 text-ink-mid" />
      </div>
      <h3 className="text-lg font-medium text-ink">No letterheads yet</h3>
      <p className="mt-1 text-sm text-ink-mid max-w-md mx-auto">
        Upload your clinic's actual letterhead once. Viyora preserves the original design and lays dynamic patient / prescription / signature fields on top.
      </p>
      <button onClick={onAdd}
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors"
              data-testid="letterheads-empty-add-btn">
        <Upload className="w-4 h-4" /> Upload your first letterhead
      </button>
    </div>
  );
}

/* ---------------------------------------------------------- upload dialog */
function UploadDialog({ onClose, onDone }) {
  const inputRef = useRef();
  const [file, setFile]   = useState(null);
  const [name, setName]   = useState("");
  const [cat, setCat]     = useState("prescription");
  const [asDefault, setAsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  const pickFile = (f) => {
    if (!f) return;
    const okExt = /\.(png|jpg|jpeg|pdf)$/i.test(f.name);
    if (!okExt) { toast.error("Only PNG, JPG, JPEG, PDF are supported."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB} MB`); return; }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a letterhead file first."); return; }
    if (!name.trim()) { toast.error("Give this letterhead a name."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      fd.append("category", cat);
      fd.append("set_default", asDefault ? "true" : "false");
      const { data } = await api.post("/letterheads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${data.letterhead?.geometry?.detected_page_size || "A4"} · ${data.letterhead?.geometry?.orientation || "portrait"}`);
      onDone();
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="lh-upload-dialog">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium text-ink">Add Letterhead</h2>
            <p className="text-xs text-ink-mid mt-0.5">PNG · JPG · PDF · max {MAX_MB} MB</p>
          </div>
          <button onClick={onClose} className="text-ink-mid hover:text-ink"><X className="w-5 h-5" /></button>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-[#E7E1D3] rounded-2xl p-8 text-center cursor-pointer hover:border-ink/30 transition-colors"
          data-testid="lh-upload-dropzone"
        >
          <input ref={inputRef} type="file" accept={ACCEPT_TYPES} onChange={(e) => pickFile(e.target.files?.[0])} className="hidden"
                 data-testid="lh-upload-file-input" />
          {file ? (
            <>
              <FileText className="w-8 h-8 text-ink mx-auto mb-2" />
              <div className="text-sm text-ink font-medium truncate">{file.name}</div>
              <div className="text-xs text-ink-mid mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB · click to change</div>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-ink-mid mx-auto mb-2" />
              <div className="text-sm text-ink">Drop a file here or click to browse</div>
              <div className="text-xs text-ink-mid mt-1">Your clinic's actual letterhead — Viyora preserves the exact design.</div>
            </>
          )}
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-ink-mid mb-1.5">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Dent-O-Sculpt Prescription"
                 className="w-full bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-ink"
                 data-testid="lh-upload-name" />
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-ink-mid mb-1.5">Category</label>
          <select value={cat} onChange={(e) => setCat(e.target.value)}
                   className="w-full bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-ink"
                   data-testid="lh-upload-category">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-ink" data-testid="lh-upload-default-label">
          <input type="checkbox" checked={asDefault} onChange={(e) => setAsDefault(e.target.checked)}
                  data-testid="lh-upload-default-cb" />
          Set as clinic default
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E7E1D3]">
          <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
          <button onClick={submit} disabled={busy || !file || !name.trim()}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="lh-upload-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ preview */
function PreviewDialog({ lh, onClose }) {
  const src = assetHref(`/api/public/letterhead-asset/${lh.id}`);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="lh-preview-dialog">
        <div className="flex items-center justify-between p-4 border-b border-[#E7E1D3]">
          <div>
            <h2 className="text-lg font-medium text-ink">{lh.name}</h2>
            <p className="text-xs text-ink-mid">{lh.geometry?.detected_page_size} · {lh.geometry?.orientation} · {lh.asset?.ext?.toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="text-ink-mid hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 bg-linen overflow-auto flex items-center justify-center p-6">
          {lh.asset?.ext === "pdf"
            ? <iframe src={src} title={lh.name} className="w-full h-full bg-white rounded-xl border border-[#E7E1D3]" />
            : <img src={src} alt={lh.name} className="max-w-full max-h-full object-contain rounded-xl bg-white border border-[#E7E1D3]" />}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ rename */
function RenameDialog({ lh, onClose, onDone }) {
  const [name, setName] = useState(lh.name);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/letterheads/${lh.id}`, { name: name.trim() });
      toast.success("Renamed");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="lh-rename-dialog">
        <h2 className="text-lg font-medium text-ink">Rename letterhead</h2>
        <input value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="w-full bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-ink"
                data-testid="lh-rename-input" autoFocus />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()}
                   className="text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full disabled:opacity-50"
                   data-testid="lh-rename-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
